// src/pages/ModulePage.js
import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/authContext';

function Pill({ children, className = '' }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${className}`}>
      {children}
    </span>
  );
}

function LevelBadge({ level }) {
  const lv = (level || 'beginner').toLowerCase();
  const cls =
    lv === 'advanced'
      ? 'bg-red-100 text-red-800 border-red-200'
      : lv === 'intermediate'
      ? 'bg-amber-100 text-amber-800 border-amber-200'
      : 'bg-emerald-100 text-emerald-800 border-emerald-200';
  const label = lv[0].toUpperCase() + lv.slice(1);
  return <Pill className={cls}>{label}</Pill>;
}

function SkeletonCard() {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 animate-pulse space-y-3">
      <div className="h-5 w-2/3 bg-gray-100 rounded" />
      <div className="h-4 w-1/3 bg-gray-100 rounded" />
      <div className="h-4 w-5/6 bg-gray-100 rounded" />
      <div className="h-8 w-24 bg-gray-100 rounded" />
    </div>
  );
}

export default function ModulePage() {
  const { moduleId } = useParams();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [moduleInfo, setModuleInfo] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [error, setError] = useState('');

  // user progress map: { [lessonId]: { completed, score, ... } }
  const [progressMap, setProgressMap] = useState({});

  // UI controls
  const [q, setQ] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [sortBy, setSortBy] = useState('default'); // 'default' | 'title' | 'newest'

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [{ data: mod, error: modErr }, { data: lessonData, error: lessonErr }] =
        await Promise.all([
          supabase.from('modules').select('*').eq('id', moduleId).maybeSingle(),
          supabase
            .from('module_lessons')
            .select('*')
            .eq('module_id', moduleId)
            .order('created_at', { ascending: true }),
        ]);

      if (modErr || !mod) throw modErr || new Error('Module not found');
      if (lessonErr) throw lessonErr;

      setModuleInfo(mod);
      setLessons(lessonData || []);
    } catch (e) {
      setError(e.message || 'Failed to load module');
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const fetchProgress = async () => {
      if (!user) return;
      const { data, error: progErr } = await supabase
        .from('module_progress')
        .select('*')
        .eq('user_id', user.id)
        .eq('module_id', moduleId);

      if (progErr) {
        // Non-fatal; just log
        console.warn('Progress fetch error:', progErr.message);
        return;
      }
      const map = {};
      (data || []).forEach((row) => {
        map[row.lesson_id] = row;
      });
      setProgressMap(map);
    };
    fetchProgress();
  }, [user, moduleId]);

  const stats = useMemo(() => {
    const total = lessons.length;
    const completed = Object.values(progressMap).filter((p) => p?.completed).length;
    const byLevel = lessons.reduce(
      (acc, l) => {
        const lv = (l.level || 'beginner').toLowerCase();
        acc[lv] = (acc[lv] || 0) + 1;
        return acc;
      },
      { beginner: 0, intermediate: 0, advanced: 0 }
    );
    const lastUpdated = lessons.reduce((max, l) => {
      const t = l.created_at ? new Date(l.created_at).getTime() : 0;
      return Math.max(max, t);
    }, moduleInfo?.created_at ? new Date(moduleInfo.created_at).getTime() : 0);

    return {
      total,
      completed,
      byLevel,
      lastUpdated: lastUpdated ? new Date(lastUpdated) : null,
    };
  }, [lessons, progressMap, moduleInfo]);

  const filteredLessons = useMemo(() => {
    let rows = [...lessons];
    const needle = q.trim().toLowerCase();

    if (levelFilter !== 'all') {
      rows = rows.filter((l) => (l.level || 'beginner').toLowerCase() === levelFilter);
    }
    if (needle) {
      rows = rows.filter((l) => {
        const hay = `${l.title || ''} ${l.reading || ''}`.toLowerCase();
        return hay.includes(needle);
      });
    }
    if (sortBy === 'title') {
      rows.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    } else if (sortBy === 'newest') {
      rows.sort(
        (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
      );
    } // default keeps creation order from query
    return rows;
  }, [lessons, q, levelFilter, sortBy]);

  const quizCount = (quiz) => {
    if (!quiz) return 0;
    try {
      // quiz could already be an object or a JSON string
      const obj = typeof quiz === 'string' ? JSON.parse(quiz) : quiz;
      if (Array.isArray(obj)) return obj.length;
      if (Array.isArray(obj?.questions)) return obj.questions.length;
      return 0;
    } catch {
      return 0;
    }
  };

  if (loading && !moduleInfo) {
    return (
      <div className="max-w-5xl mx-auto space-y-8 p-4">
        <SkeletonCard />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (!moduleInfo) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <p className="text-red-600">{error || 'Module not found.'}</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Module Header */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{moduleInfo.name}</h1>
            {moduleInfo.description && (
              <p className="mt-2 text-gray-700">{moduleInfo.description}</p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Pill className="bg-blue-50 text-blue-700 border-blue-200">
                {stats.total} lesson{stats.total === 1 ? '' : 's'}
              </Pill>
              {stats.completed > 0 && (
                <Pill className="bg-emerald-50 text-emerald-700 border-emerald-200">
                  {stats.completed} completed
                </Pill>
              )}
              {stats.lastUpdated && (
                <Pill className="bg-gray-50 text-gray-700 border-gray-200">
                  Updated {stats.lastUpdated.toLocaleDateString()}
                </Pill>
              )}
              {/* Aggregate level snapshot */}
              <Pill className="bg-amber-50 text-amber-800 border-amber-200">
                Levels: B {stats.byLevel.beginner} · I {stats.byLevel.intermediate} · A{' '}
                {stats.byLevel.advanced}
              </Pill>
              {moduleInfo.is_public ? (
                <Pill className="bg-indigo-50 text-indigo-700 border-indigo-200">Public</Pill>
              ) : (
                <Pill className="bg-gray-50 text-gray-700 border-gray-200">Locked</Pill>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid sm:grid-cols-6 gap-3">
          <div className="sm:col-span-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Search lessons</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Search by title or reading text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Level</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <div className="sm:col-span-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Sort</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="default">Default</option>
              <option value="title">Title A to Z</option>
              <option value="newest">Newest first</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lessons List */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">Lessons</h2>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={`ls-${i}`} />
            ))}
          </div>
        ) : filteredLessons.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-gray-700">
            {q || levelFilter !== 'all'
              ? 'No lessons match your filters.'
              : 'No lessons yet in this module.'}
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredLessons.map((lesson, i) => {
              const progress = progressMap[lesson.id];
              const completed = !!progress?.completed;
              const score = progress?.score;
              const qc = quizCount(lesson.quiz);

              return (
                <li key={lesson.id} className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 truncate">
                        Lesson {i + 1}: {lesson.title}
                      </h3>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <LevelBadge level={lesson.level} />
                        {qc > 0 && (
                          <Pill className="bg-blue-50 text-blue-700 border-blue-200">
                            {qc} quiz
                            {qc === 1 ? '' : 'zes'}
                          </Pill>
                        )}
                        {lesson.allow_writing && (
                          <Pill className="bg-purple-50 text-purple-700 border-purple-200">
                            Writing
                          </Pill>
                        )}
                        {lesson.allow_case_builder && (
                          <Pill className="bg-rose-50 text-rose-700 border-rose-200">
                            Case Builder
                          </Pill>
                        )}
                      </div>
                      <p className="mt-2 text-xs text-gray-600 line-clamp-3">
                        {lesson.reading || 'No reading summary.'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      {completed ? (
                        <span>
                          ✅ Completed{typeof score === 'number' ? ` – Score: ${score}%` : ''}
                        </span>
                      ) : (
                        <span>Not started</span>
                      )}
                    </div>

                    <Link
                      to={`/modules/view/${moduleId}/lesson/${lesson.id}`}
                      className={`px-3 py-1.5 rounded text-sm ${
                        completed
                          ? 'bg-gray-800 text-white hover:bg-gray-700'
                          : 'bg-blue-600 text-white hover:bg-blue-500'
                      }`}
                    >
                      {completed ? 'View' : 'Start'}
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}
